import React, { useState, useMemo } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { apiMergeDepartments } from "./api";

// MergeDepartmentModal — Super Admin merges this department into another one.
// Shows a strong confirmation summary with exact counts so the user knows
// what will be moved BEFORE confirming.
export default function MergeDepartmentModal({ source, candidates, memberCount, positionCount, assignmentCount, announcementCount, onMerged, onClose }) {
  const [targetId, setTargetId] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const target = useMemo(() => candidates.find(c => c.id === targetId) || null, [candidates, targetId]);
  const submit = async () => {
    setError("");
    if (!targetId) { setError("Please pick a target department."); return; }
    if (!confirm) { setError("Please confirm you understand this is permanent."); return; }
    setSaving(true);
    try {
      const result = await apiMergeDepartments(source.id, targetId);
      onMerged && onMerged(result);
    } catch (e) {
      setError(e.message || "Merge failed");
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="dept-merge-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Merge &ldquo;{source.name}&rdquo; into…</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{
            background: "rgba(220,90,90,0.10)", border: "1px solid rgba(220,90,90,0.40)",
            color: "#E58E8E", borderRadius: 10, padding: "10px 14px", marginBottom: 16,
            fontFamily: "'Montserrat',sans-serif", fontSize: 12, lineHeight: 1.5,
          }}>
            <strong style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}><CustomIcon name="warning" size={14} /> This action is permanent.</strong>
            All data below will move into the target department and <strong>&ldquo;{source.name}&rdquo;</strong> will be deleted.
          </div>

          <div className="form-group mb-14">
            <label>Target Department *</label>
            <select data-testid="dept-merge-target" value={targetId} onChange={e => { setTargetId(e.target.value); if (error) setError(""); }}>
              <option value="">— Choose a department —</option>
              {candidates.map(d => (
                <option key={d.id} value={d.id}>{d.name}{d.overrideActive ? " (override active)" : ""}</option>
              ))}
            </select>
          </div>

          <div className="card" style={{ padding: 12, marginBottom: 14, background: "var(--kc-surface)" }}>
            <div style={{ fontSize: 10, letterSpacing: "0.1em", color: "var(--kc-accent)", fontWeight: 800, textTransform: "uppercase", marginBottom: 8, fontFamily: "'Montserrat',sans-serif" }}>
              Will be moved from &ldquo;{source.name}&rdquo;{target ? ` to "${target.name}"` : ""}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-text)" }}>
              <div data-testid="dept-merge-count-members"><strong style={{ color: "var(--kc-accent)" }}>{memberCount}</strong> member{memberCount === 1 ? "" : "s"}</div>
              <div data-testid="dept-merge-count-positions"><strong style={{ color: "var(--kc-accent)" }}>{positionCount}</strong> position{positionCount === 1 ? "" : "s"}</div>
              <div data-testid="dept-merge-count-assignments"><strong style={{ color: "var(--kc-accent)" }}>{assignmentCount}</strong> assignment{assignmentCount === 1 ? "" : "s"}</div>
              <div data-testid="dept-merge-count-announcements"><strong style={{ color: "var(--kc-accent)" }}>{announcementCount}</strong> announcement{announcementCount === 1 ? "" : "s"}</div>
            </div>
          </div>

          {source.overrideActive && (
            <div style={{
              background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent)",
              color: "var(--kc-accent)", borderRadius: 8, padding: "8px 12px", marginBottom: 14,
              fontFamily: "'Montserrat',sans-serif", fontSize: 12,
            }}>
              The active priority override on &ldquo;{source.name}&rdquo; will be archived into the target&apos;s history.
            </div>
          )}

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "8px 0" }}>
            <input
              type="checkbox"
              data-testid="dept-merge-confirm"
              checked={confirm}
              onChange={e => { setConfirm(e.target.checked); if (error) setError(""); }}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: "var(--kc-text)", lineHeight: 1.4 }}>
              I understand this is permanent and &ldquo;{source.name}&rdquo; will be deleted after the merge completes.
            </span>
          </label>

          {error && (
            <div
              data-testid="dept-merge-error"
              role="alert"
              style={{
                marginTop: 10,
                background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
                color: "var(--kc-accent)",
                borderRadius: 8, padding: "8px 12px",
                fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-danger"
            data-testid="dept-merge-confirm-btn"
            disabled={saving || !targetId || !confirm}
            onClick={submit}
          >
            {saving ? "Merging…" : "Merge & Delete Source"}
          </button>
        </div>
      </div>
    </div>
  );
}
